import { usePrivy } from '@privy-io/react-auth';
import { useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';

import { useToast } from '@/components/toast/useToast';
import { useJoinTableBatchSession } from '@/features/account-abstraction/hooks/useJoinTableBatchSession';
import { useRebuyBatchSession } from '@/features/account-abstraction/hooks/useRebuyBatchSession';
import { useSessionKeyAndStorageClient } from '@/features/account-abstraction/hooks/useSessionKeyAndStorageClient';
import { useSmartAccountInitialization } from '@/features/account-abstraction/hooks/useSmartAccountInitialization';
import useAutoTopUp from '@/features/poker-table/hooks/useAutoTopUp';
import { useDocumentTitle } from '@/hooks/useDocumentTitle';
import useWindowFocus from '@/hooks/useWindowFocus';
import { chatConnectAction } from '@/services/chat/chatActions';
import { ReducerState } from '@/services/common/lib/reducerState';
import { LobbyTableOpenBuyInAction } from '@/services/lobby/lobbyActions';
import { setWaitForBBAction, tableConnectAction, tableInit } from '@/services/table/tableActions';
import { sitInRest } from '@/services/table/tableService';
import * as Sentry from '@sentry/react';

import Table from './components/templates/Table';
import {
  selectCanWaitForBigBlind,
  selectPlayerIsSitout,
  selectPlayerIsWaiting,
  selectPlayerSeat,
  selectTableStateUndefined,
  selectTableTitle,
  selectTableUrl,
} from '../../services/table/tableSelectors';

const TablePage = () => {
  const { tableId } = useParams();

  if (!tableId) {
    throw new Error('No tableId');
  }

  const user = useSelector((state: ReducerState) => state.auth);
  const userId: string = user.id as string;

  if (!userId) {
    throw new Error('No userId');
  }

  const { getAccessToken } = usePrivy();
  useAutoTopUp();
  useSmartAccountInitialization();
  useSessionKeyAndStorageClient();
  useJoinTableBatchSession();
  useRebuyBatchSession();

  const { toast } = useToast();

  const isFocused = useWindowFocus();

  const dispatch = useDispatch();

  const tableStateIsUndefined = useSelector(selectTableStateUndefined(tableId));
  const playerSeat = useSelector(selectPlayerSeat(tableId));
  const tableUrl = useSelector(selectTableUrl(tableId));
  const playerIsWaiting = useSelector(selectPlayerIsWaiting(tableId));
  const tableTitle = useSelector(selectTableTitle(tableId));
  const isSitout = useSelector(selectPlayerIsSitout(tableId));
  const canWaitForBigBlind = useSelector(selectCanWaitForBigBlind(tableId));

  useDocumentTitle(tableTitle);

  const [waitingForBigBlind, setWaitingForBigBlind] = useState<boolean | undefined>(playerIsWaiting);

  useEffect(() => {
    setWaitingForBigBlind(playerIsWaiting);
  }, [playerIsWaiting]);

  useEffect(() => {
    // check if the user is still authenticated when the window is focused
    if (isFocused) {
      getAccessToken().then((token: string | null) => {
        if (!token) {
          window.location.reload();
        }
      });
    }
  }, [isFocused, getAccessToken]);

  useEffect(() => {
    if (!window.location.hash && window.addEventListener) {
      window.addEventListener('load', function () {
        setTimeout(function () {
          window.scrollTo(0, 0);
        }, 0);
      });
      window.addEventListener('orientationchange', function () {
        setTimeout(function () {
          window.scrollTo(0, 0);
        }, 0);
      });
      window.addEventListener('touchstart', function () {
        setTimeout(function () {
          window.scrollTo(0, 0);
        }, 0);
      });
    }
  }, []);

  useEffect(() => {
    if (tableId && !tableUrl) {
      dispatch(tableInit(tableId));
    }
  }, [dispatch, tableId, tableUrl]);

  useEffect(() => {
    if (tableId && tableUrl && userId) {
      dispatch(tableConnectAction(tableId, tableUrl));
      dispatch(chatConnectAction(tableId, tableUrl));
    }
  }, [dispatch, tableId, tableUrl, userId]);

  const handleSitIn = useCallback(async () => {
    try {
      if (playerSeat?.stack === 0) {
        dispatch(
          LobbyTableOpenBuyInAction({
            tableId,
            seat: playerSeat?.seat,
            stack: 0,
            seatReserved: true,
            isRebuy: true,
            sitOutOnCancel: true,
          }),
        );
      }
      await sitInRest({
        tableId: tableId,
        tableUrl: tableUrl,
      });
    } catch (err) {
      toast({ description: 'Error when sitting in' }); // TODO : DISPLAY APPROPRIATE MESSAGE
      Sentry.captureException(err);
    }
  }, [tableId, tableUrl]);

  const handleBuyIn = useCallback(
    (tableId: string, seat: number, stack: number) => {
      dispatch(
        LobbyTableOpenBuyInAction({
          tableId,
          seat,
          stack,
        }),
      );
    },
    [dispatch],
  );

  const handlePostNextHand = () => {
    dispatch(setWaitForBBAction(tableId, false));
    setWaitingForBigBlind(false);
  };

  const handleWaitForBigBlind = () => {
    dispatch(setWaitForBBAction(tableId, true));
    setWaitingForBigBlind(true);
  };

  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const seat = searchParams.get('seat');
    const stack = searchParams.get('stack');

    if (tableId && seat && stack) {
      handleBuyIn(tableId, parseInt(seat), parseInt(stack));
    }
  }, [tableId, handleBuyIn, location.search]);

  return !tableId || tableStateIsUndefined ? null : (
    <Table
      canWaitForBigBlind={canWaitForBigBlind}
      isSitout={isSitout}
      onPostNextHand={handlePostNextHand}
      onWaitForBigBlind={handleWaitForBigBlind}
      onSitIn={handleSitIn}
      tableId={tableId}
      userId={userId}
      user={user}
      waitingForBigBlind={!!waitingForBigBlind}
    />
  );
};

export default TablePage;
